import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { withAuth } from '../../hoc/Auth';
import Api from '../../Api';
import classes from './styles.css';

const POLL_OPEN_MS = 7000;
const POLL_MIN_MS = 30000;
const VIEW_LIST = 'list';
const VIEW_CHAT = 'chat';
const VIEW_NEW = 'new';
const EDIT_WINDOW_SEC = 15 * 60;

/* ---------- icons (inline SVG, currentColor for theming) ---------- */

const IconChat = ({ size = 26 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M4 5.5C4 4.12 5.12 3 6.5 3h11C18.88 3 20 4.12 20 5.5v8c0 1.38-1.12 2.5-2.5 2.5H10l-4 4v-4h-1.5C5.12 16 4 14.88 4 13.5v-8z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
            fill="rgba(255,255,255,0.04)"
        />
        <circle cx="9" cy="9.5" r="1" fill="currentColor" />
        <circle cx="12" cy="9.5" r="1" fill="currentColor" />
        <circle cx="15" cy="9.5" r="1" fill="currentColor" />
    </svg>
);

const IconChevronDown = ({ size = 22 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const IconArrowLeft = ({ size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const IconPaperclip = ({ size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M21.44 11.05l-9.19 9.19a5.5 5.5 0 01-7.78-7.78l9.19-9.19a3.5 3.5 0 014.95 4.95l-8.83 8.83a1.5 1.5 0 01-2.12-2.12l7.41-7.41"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const IconSend = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M22 2l-7 20-4-9-9-4 20-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const IconPlus = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

const IconHeader = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M4 5.5C4 4.12 5.12 3 6.5 3h11C18.88 3 20 4.12 20 5.5v8c0 1.38-1.12 2.5-2.5 2.5H10l-4 4v-4h-1.5C5.12 16 4 14.88 4 13.5v-8z"
            stroke="#fff"
            strokeWidth="1.6"
            strokeLinejoin="round"
            fill="rgba(255,255,255,0.12)"
        />
    </svg>
);

const IconFile = ({ size = 14 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M14 2v6h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const IconEmptyChat = () => (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M8 13.5C8 11.01 10.01 9 12.5 9h23C37.99 9 40 11.01 40 13.5v17C40 33 37.99 35 35.5 35H22l-8 8v-8h-1.5C10.01 35 8 33 8 30.5v-17z"
            fill="#6366f1"
            opacity="0.18"
        />
        <path
            d="M8 13.5C8 11.01 10.01 9 12.5 9h23C37.99 9 40 11.01 40 13.5v17C40 33 37.99 35 35.5 35H22l-8 8v-8h-1.5C10.01 35 8 33 8 30.5v-17z"
            stroke="#6366f1"
            strokeWidth="2"
            strokeLinejoin="round"
        />
        <circle cx="17" cy="22" r="1.6" fill="#6366f1" />
        <circle cx="24" cy="22" r="1.6" fill="#6366f1" />
        <circle cx="31" cy="22" r="1.6" fill="#6366f1" />
    </svg>
);

/* ---------- helpers ---------- */

function statusLabel(status, intl) {
    if (status === 'open') return intl.formatMessage({ id: 'SupportWidget.status.open', defaultMessage: 'Открыто' });
    if (status === 'pending_user') return intl.formatMessage({ id: 'SupportWidget.status.pendingUser', defaultMessage: 'Ожидает ответа' });
    if (status === 'closed') return intl.formatMessage({ id: 'SupportWidget.status.closed', defaultMessage: 'Закрыто' });
    return status;
}

function statusClass(status) {
    if (status === 'open') return classes.StatusOpen;
    if (status === 'pending_user') return classes.StatusPendingUser;
    if (status === 'closed') return classes.StatusClosed;
    return classes.StatusOpen;
}

function timeAgo(iso, intl) {
    if (!iso) return '';
    const dt = new Date(iso);
    const sec = Math.max(1, Math.floor((Date.now() - dt.getTime()) / 1000));
    if (sec < 60) return intl.formatMessage({ id: 'SupportWidget.time.justNow', defaultMessage: 'только что' });
    if (sec < 3600) return intl.formatMessage({ id: 'SupportWidget.time.minutesAgo', defaultMessage: '{count} мин назад' }, { count: Math.floor(sec / 60) });
    if (sec < 86400) return intl.formatMessage({ id: 'SupportWidget.time.hoursAgo', defaultMessage: '{count} ч назад' }, { count: Math.floor(sec / 3600) });
    if (sec < 86400 * 7) return intl.formatMessage({ id: 'SupportWidget.time.daysAgo', defaultMessage: '{count} д назад' }, { count: Math.floor(sec / 86400) });
    return intl.formatDate(dt);
}

function fmtSize(bytes, intl) {
    if (!bytes) return '';
    if (bytes < 1024) return intl.formatMessage({ id: 'SupportWidget.size.bytes', defaultMessage: '{count} б' }, { count: bytes });
    if (bytes < 1024 * 1024) return intl.formatMessage({ id: 'SupportWidget.size.kb', defaultMessage: '{count} КБ' }, { count: (bytes / 1024).toFixed(1) });
    return intl.formatMessage({ id: 'SupportWidget.size.mb', defaultMessage: '{count} МБ' }, { count: (bytes / 1024 / 1024).toFixed(1) });
}

function avatarInitials(name) {
    if (!name) return '?';
    const parts = String(name).trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
}

/* ---------- main ---------- */

const SupportWidget = ({ token, isAuthorized, adminPrivileges }) => {
    const intl = useIntl();
    const [open, setOpen] = useState(false);
    const [view, setView] = useState(VIEW_LIST);
    const [tickets, setTickets] = useState([]);
    const [activeTicket, setActiveTicket] = useState(null);
    const [messages, setMessages] = useState([]);
    const [draftBody, setDraftBody] = useState('');
    const [draftSubject, setDraftSubject] = useState('');
    const [draftFiles, setDraftFiles] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [sending, setSending] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editingBody, setEditingBody] = useState('');
    const [error, setError] = useState(null);
    const [dragging, setDragging] = useState(false);

    const fileInputRef = useRef(null);
    const lastUpdatedRef = useRef(null);
    const bodyRef = useRef(null);
    const location = useLocation();

    const api = useMemo(() => (token ? new Api({ token }) : null), [token]);
    const isMerchantAuth = isAuthorized && !adminPrivileges;

    /* ----- data loading ----- */

    const loadTickets = useCallback(async () => {
        if (!api) return;
        try {
            const data = await api.listMerchantTickets();
            setTickets(data.items || []);
        } catch (e) { /* silent */ }
    }, [api]);

    const loadMessages = useCallback(async (ticketId, useSince) => {
        if (!api) return;
        try {
            const since = useSince ? lastUpdatedRef.current : null;
            const data = await api.getMerchantTicketMessages(ticketId, since);
            if (since) {
                if ((data.messages || []).length > 0) {
                    setMessages((prev) => {
                        const map = new Map();
                        prev.forEach((m) => map.set(m.id, m));
                        (data.messages || []).forEach((m) => map.set(m.id, m));
                        return Array.from(map.values()).sort((a, b) => a.id - b.id);
                    });
                }
            } else {
                setMessages(data.messages || []);
            }
            const newest = (data.messages || []).reduce(
                (acc, m) => (acc && acc > m.updated_at ? acc : m.updated_at),
                lastUpdatedRef.current
            );
            if (newest) lastUpdatedRef.current = newest;
        } catch (e) { /* silent */ }
    }, [api]);

    const loadUnreadCount = useCallback(async () => {
        if (!api) return;
        try {
            const data = await api.getMerchantTicketsUnreadCount();
            setUnreadCount(data.count || 0);
        } catch (e) { /* silent */ }
    }, [api]);

    useEffect(() => {
        if (!isMerchantAuth) return undefined;
        loadUnreadCount();
        const i = setInterval(loadUnreadCount, POLL_MIN_MS);
        return () => clearInterval(i);
    }, [isMerchantAuth, loadUnreadCount]);

    useEffect(() => {
        if (!open || !activeTicket) return undefined;
        loadMessages(activeTicket.id, false);
        const i = setInterval(() => loadMessages(activeTicket.id, true), POLL_OPEN_MS);
        return () => clearInterval(i);
    }, [open, activeTicket, loadMessages]);

    useEffect(() => {
        if (!open || view !== VIEW_LIST) return;
        loadTickets();
    }, [open, view, loadTickets]);

    // auto-scroll chat to bottom when messages change
    useEffect(() => {
        if (view === VIEW_CHAT && bodyRef.current) {
            bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
        }
    }, [messages, view]);

    // listen for global event to open widget (from Support menu button)
    useEffect(() => {
        if (!isMerchantAuth) return;
        const handler = () => {
            setOpen(true);
            setView(VIEW_LIST);
        };
        window.addEventListener('open-support-widget', handler);
        return () => window.removeEventListener('open-support-widget', handler);
    }, [isMerchantAuth]);

    // deep-link from email: ?ticket=ID auto-opens that ticket
    useEffect(() => {
        if (!isMerchantAuth) return;
        const search = new URLSearchParams(location.search);
        const tid = search.get('ticket');
        if (!tid) return;
        const ticketId = parseInt(tid, 10);
        if (!ticketId) return;
        (async () => {
            if (!api) return;
            try {
                const data = await api.getMerchantTicket(ticketId);
                if (data && data.ticket) {
                    setOpen(true);
                    setView(VIEW_CHAT);
                    setActiveTicket(data.ticket);
                    setMessages(data.messages || []);
                    lastUpdatedRef.current = (data.messages || []).reduce(
                        (acc, m) => (acc && acc > m.updated_at ? acc : m.updated_at),
                        null
                    );
                    api.markMerchantTicketRead(ticketId).catch(() => {});
                }
            } catch (e) { /* silent */ }
        })();
    }, [isMerchantAuth, api, location.search]);

    if (!isMerchantAuth) return null;

    /* ----- actions ----- */

    const openTicket = async (ticket) => {
        setView(VIEW_CHAT);
        setActiveTicket(ticket);
        setMessages([]);
        lastUpdatedRef.current = null;
        await loadMessages(ticket.id, false);
        try {
            await api.markMerchantTicketRead(ticket.id);
            loadUnreadCount();
        } catch (e) { /* silent */ }
    };

    const backToList = () => {
        setView(VIEW_LIST);
        setActiveTicket(null);
        setMessages([]);
        lastUpdatedRef.current = null;
        setEditingId(null);
        setError(null);
        loadTickets();
        loadUnreadCount();
    };

    const startNewTicket = () => {
        setView(VIEW_NEW);
        setDraftSubject('');
        setDraftBody('');
        setDraftFiles([]);
        setError(null);
    };

    const onPickFiles = (filesList) => {
        const arr = Array.from(filesList || []);
        const total = draftFiles.length + arr.length;
        if (total > 10) {
            setError(intl.formatMessage({ id: 'SupportWidget.error.maxFiles', defaultMessage: 'Не более 10 файлов на сообщение.' }));
            return;
        }
        for (const f of arr) {
            if (f.size > 25 * 1024 * 1024) {
                setError(
                    intl.formatMessage(
                        { id: 'SupportWidget.error.fileTooLarge', defaultMessage: 'Файл "{name}" больше 25 МБ.' },
                        { name: f.name },
                    ),
                );
                return;
            }
        }
        setError(null);
        setDraftFiles([...draftFiles, ...arr]);
    };

    const removeDraftFile = (idx) => {
        const next = [...draftFiles];
        next.splice(idx, 1);
        setDraftFiles(next);
    };

    const submitNewTicket = async () => {
        if (!draftSubject.trim()) {
            setError(intl.formatMessage({ id: 'SupportWidget.error.subjectRequired', defaultMessage: 'Укажите тему обращения.' }));
            return;
        }
        if (!draftBody.trim() && draftFiles.length === 0) {
            setError(
                intl.formatMessage({
                    id: 'SupportWidget.error.bodyOrFileRequired',
                    defaultMessage: 'Опишите ваш вопрос или прикрепите файл.',
                }),
            );
            return;
        }
        setSending(true);
        setError(null);
        try {
            const data = await api.createMerchantTicket(draftSubject.trim(), draftBody, draftFiles);
            setDraftSubject('');
            setDraftBody('');
            setDraftFiles([]);
            await loadTickets();
            if (data && data.ticket) {
                openTicket(data.ticket);
            } else {
                setView(VIEW_LIST);
            }
        } catch (e) {
            setError((e.response && e.response.data && e.response.data.message) || intl.formatMessage({ id: 'SupportWidget.error.send', defaultMessage: 'Не удалось отправить.' }));
        } finally {
            setSending(false);
        }
    };

    const submitMessage = async () => {
        if (!activeTicket) return;
        if (!draftBody.trim() && draftFiles.length === 0) return;
        setSending(true);
        setError(null);
        try {
            await api.postMerchantTicketMessage(activeTicket.id, draftBody, draftFiles);
            setDraftBody('');
            setDraftFiles([]);
            await loadMessages(activeTicket.id, false);
        } catch (e) {
            setError((e.response && e.response.data && e.response.data.message) || intl.formatMessage({ id: 'SupportWidget.error.send', defaultMessage: 'Не удалось отправить.' }));
        } finally {
            setSending(false);
        }
    };

    const startEdit = (m) => { setEditingId(m.id); setEditingBody(m.body); };
    const cancelEdit = () => { setEditingId(null); setEditingBody(''); };
    const saveEdit = async () => {
        if (!editingId || !activeTicket) return;
        try {
            await api.editMerchantTicketMessage(activeTicket.id, editingId, editingBody);
            cancelEdit();
            await loadMessages(activeTicket.id, false);
        } catch (e) {
            setError((e.response && e.response.data && e.response.data.message) || intl.formatMessage({ id: 'SupportWidget.error.save', defaultMessage: 'Не удалось сохранить.' }));
        }
    };

    const downloadAttachment = (att) => {
        if (att.purged || !activeTicket) return;
        api.downloadMerchantTicketAttachment(activeTicket.id, att.id, att.filename).catch(() => {});
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        if (e.dataTransfer && e.dataTransfer.files) {
            onPickFiles(e.dataTransfer.files);
        }
    };

    /* ----- renderers ----- */

    const renderMessage = (m, lastOwnId) => {
        const isMine = m.author_type === 'user';
        const isLastMine = isMine && m.id === lastOwnId;
        const ageSec = (Date.now() - new Date(m.created_at).getTime()) / 1000;
        const editable = isLastMine && !m.deleted && ageSec <= EDIT_WINDOW_SEC;

        return (
            <div key={m.id} className={`${classes.MsgRow} ${isMine ? classes.MsgRowMine : ''}`}>
                {!isMine && (
                    <div className={`${classes.Avatar} ${classes.AvatarSupport}`}>S</div>
                )}
                <div className={classes.MsgGroup}>
                    <div className={`${classes.MsgBubble} ${isMine ? classes.MsgBubbleMine : ''} ${m.deleted ? classes.MsgBubbleDeleted : ''}`}>
                        {editingId === m.id ? (
                            <div className={classes.EditForm}>
                                <textarea
                                    className={classes.EditFormArea}
                                    value={editingBody}
                                    onChange={(e) => setEditingBody(e.target.value)}
                                />
                                <div className={classes.EditActions}>
                                    <button className={classes.EditSave} onClick={saveEdit}>
                                        {intl.formatMessage({ id: 'SupportWidget.edit.save', defaultMessage: 'Сохранить' })}
                                    </button>
                                    <button className={classes.EditCancel} onClick={cancelEdit}>
                                        {intl.formatMessage({ id: 'SupportWidget.edit.cancel', defaultMessage: 'Отмена' })}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <Fragment>
                                {m.deleted
                                    ? intl.formatMessage({ id: 'SupportWidget.message.deleted', defaultMessage: 'сообщение удалено' })
                                    : m.body}
                                {(m.attachments || []).map((a) =>
                                    a.purged ? (
                                        <span key={a.id} className={classes.AttachmentPurged}>
                                            <IconFile />{' '}
                                            {intl.formatMessage({
                                                id: 'SupportWidget.attachment.purged',
                                                defaultMessage: 'файл удалён по сроку хранения',
                                            })}
                                        </span>
                                    ) : (
                                        <button
                                            key={a.id}
                                            type="button"
                                            className={classes.AttachmentChip}
                                            onClick={() => downloadAttachment(a)}
                                            title={`${a.filename} · ${fmtSize(a.size_bytes, intl)}`}
                                        >
                                            <IconFile /> {a.filename}
                                        </button>
                                    )
                                )}
                            </Fragment>
                        )}
                    </div>
                    <div className={classes.MsgFooter}>
                        <span>{timeAgo(m.created_at, intl)}</span>
                        {m.edited && (
                            <span className={classes.MsgEditedTag}>
                                · {intl.formatMessage({ id: 'SupportWidget.message.edited', defaultMessage: 'изменено' })}
                            </span>
                        )}
                        {editable && (
                            <button
                                className={classes.EditMsgBtn}
                                onClick={() => startEdit(m)}
                                title={intl.formatMessage({ id: 'SupportWidget.edit.title', defaultMessage: 'Редактировать' })}
                            >
                                ✎
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const lastOwnId = (() => {
        for (let i = messages.length - 1; i >= 0; i--) {
            if (messages[i].author_type === 'user' && !messages[i].deleted) return messages[i].id;
        }
        return null;
    })();

    const headerTitle = view === VIEW_LIST ? intl.formatMessage({ id: 'SupportWidget.header.listTitle', defaultMessage: 'Поддержка' })
        : view === VIEW_CHAT && activeTicket ? activeTicket.subject
        : intl.formatMessage({ id: 'SupportWidget.header.newTitle', defaultMessage: 'Новое обращение' });

    const headerSubtitle = view === VIEW_LIST ? intl.formatMessage({ id: 'SupportWidget.header.listSubtitle', defaultMessage: 'Мы обычно отвечаем в течение часа' })
        : view === VIEW_CHAT ? null
        : intl.formatMessage({ id: 'SupportWidget.header.newSubtitle', defaultMessage: 'Опишите ваш вопрос, мы поможем' });

    const showComposer = view === VIEW_NEW
        || (view === VIEW_CHAT && activeTicket && activeTicket.status !== 'closed');

    return (
        <Fragment>
            <button
                className={classes.Fab}
                onClick={() => setOpen(!open)}
                title={open
                    ? intl.formatMessage({ id: 'SupportWidget.action.collapse', defaultMessage: 'Свернуть' })
                    : intl.formatMessage({ id: 'SupportWidget.header.listTitle', defaultMessage: 'Поддержка' })}
                aria-label={intl.formatMessage({ id: 'SupportWidget.header.listTitle', defaultMessage: 'Поддержка' })}
            >
                {open ? <IconChevronDown /> : <IconChat />}
                {!open && unreadCount > 0 && <span className={classes.Badge}>{unreadCount}</span>}
            </button>

            {open && (
                <div className={classes.Panel}>
                    <div className={classes.Header}>
                        <div className={classes.HeaderTopRow}>
                            {(view === VIEW_CHAT || view === VIEW_NEW) ? (
                                <button
                                    className={classes.HeaderBtn}
                                    onClick={view === VIEW_NEW ? () => setView(VIEW_LIST) : backToList}
                                    title={intl.formatMessage({ id: 'SupportWidget.action.back', defaultMessage: 'Назад' })}
                                >
                                    <IconArrowLeft />
                                </button>
                            ) : (
                                <div className={classes.HeaderIcon}><IconHeader /></div>
                            )}
                            <div className={classes.HeaderTitleBlock}>
                                <div className={classes.HeaderTitle}>{headerTitle}</div>
                                {headerSubtitle && <div className={classes.HeaderSubtitle}>{headerSubtitle}</div>}
                                {view === VIEW_CHAT && activeTicket && (
                                    <span className={classes.StatusChipHeader} style={{ marginTop: 6 }}>
                                        {statusLabel(activeTicket.status, intl)}
                                    </span>
                                )}
                            </div>
                            <button
                                className={classes.HeaderBtn}
                                onClick={() => setOpen(false)}
                                title={intl.formatMessage({ id: 'SupportWidget.action.collapse', defaultMessage: 'Свернуть' })}
                            >
                                <IconChevronDown size={18} />
                            </button>
                        </div>
                    </div>

                    <div
                        ref={bodyRef}
                        className={`${classes.Body} ${dragging ? classes.BodyDropActive : ''}`}
                        onDragOver={(e) => { if (view !== VIEW_LIST) { e.preventDefault(); setDragging(true); } }}
                        onDragLeave={() => setDragging(false)}
                        onDrop={onDrop}
                    >
                        {view === VIEW_LIST && (
                            tickets.length === 0 ? (
                                <div className={classes.Empty}>
                                    <div className={classes.EmptyIllustration}><IconEmptyChat /></div>
                                    <div className={classes.EmptyTitle}>
                                        {intl.formatMessage({ id: 'SupportWidget.empty.listTitle', defaultMessage: 'Задайте вопрос поддержке' })}
                                    </div>
                                    <div className={classes.EmptyText}>
                                        {intl.formatMessage({
                                            id: 'SupportWidget.empty.listText',
                                            defaultMessage: 'Опишите проблему, прикрепите скриншоты или документы — мы ответим как можно быстрее.',
                                        })}
                                    </div>
                                    <button className={classes.NewBtn} onClick={startNewTicket}>
                                        <IconPlus />
                                        {intl.formatMessage({ id: 'SupportWidget.action.createTicket', defaultMessage: 'Создать обращение' })}
                                    </button>
                                </div>
                            ) : (
                                <Fragment>
                                    <button
                                        className={`${classes.NewBtn} ${classes.NewBtnFull}`}
                                        onClick={startNewTicket}
                                    >
                                        <IconPlus />
                                        {intl.formatMessage({ id: 'SupportWidget.action.createTicket', defaultMessage: 'Создать обращение' })}
                                    </button>
                                    {tickets.map((t) => (
                                        <div className={classes.TicketCard} key={t.id} onClick={() => openTicket(t)}>
                                            <div className={classes.TicketCardTop}>
                                                <div className={`${classes.TicketCardSubject} ${t.unread ? classes.TicketCardSubjectUnread : ''}`}>
                                                    {t.subject}
                                                </div>
                                                {t.unread && <span className={classes.UnreadDot} />}
                                            </div>
                                            <div className={classes.TicketCardMeta}>
                                                <span className={`${classes.StatusChip} ${statusClass(t.status)}`}>
                                                    {statusLabel(t.status, intl)}
                                                </span>
                                                <span>{timeAgo(t.last_message_at || t.updated_at, intl)}</span>
                                            </div>
                                        </div>
                                    ))}
                                </Fragment>
                            )
                        )}

                        {view === VIEW_CHAT && (
                            messages.length === 0 ? (
                                <div className={classes.Empty}>
                                    <div className={classes.EmptyIllustration}><IconEmptyChat /></div>
                                    <div className={classes.EmptyText}>
                                        {intl.formatMessage({ id: 'SupportWidget.empty.chat', defaultMessage: 'Начните диалог — сообщения появятся здесь.' })}
                                    </div>
                                </div>
                            ) : (
                                <Fragment>{messages.map((m) => renderMessage(m, lastOwnId))}</Fragment>
                            )
                        )}

                        {view === VIEW_NEW && (
                            <div className={classes.NewForm}>
                                <input
                                    className={classes.SubjectInput}
                                    placeholder={intl.formatMessage({ id: 'SupportWidget.placeholder.subject', defaultMessage: 'Тема обращения' })}
                                    value={draftSubject}
                                    onChange={(e) => setDraftSubject(e.target.value.slice(0, 120))}
                                    maxLength={120}
                                    autoFocus
                                />
                                <textarea
                                    className={classes.NewBodyArea}
                                    placeholder={intl.formatMessage({ id: 'SupportWidget.placeholder.details', defaultMessage: 'Опишите ваш вопрос подробнее...' })}
                                    value={draftBody}
                                    onChange={(e) => setDraftBody(e.target.value)}
                                />
                                <div className={classes.FormHint}>
                                    {intl.formatMessage({
                                        id: 'SupportWidget.hint.attachments',
                                        defaultMessage: 'Можно прикрепить до 10 файлов (до 25 МБ каждый): скриншоты, документы, видео.',
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    {showComposer && (
                        <div className={classes.Composer}>
                            {error && <div className={classes.ErrorMsg}>{error}</div>}
                            {draftFiles.length > 0 && (
                                <div className={classes.AttachPreviewList}>
                                    {draftFiles.map((f, idx) => (
                                        <div key={idx} className={classes.AttachPreview}>
                                            <IconFile /> {f.name} · {fmtSize(f.size, intl)}
                                            <button className={classes.AttachRemove} onClick={() => removeDraftFile(idx)}>×</button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {view === VIEW_CHAT && (
                                <textarea
                                    className={classes.ComposerArea}
                                    placeholder={intl.formatMessage({ id: 'SupportWidget.placeholder.message', defaultMessage: 'Сообщение...' })}
                                    value={draftBody}
                                    onChange={(e) => setDraftBody(e.target.value)}
                                    rows={2}
                                />
                            )}
                            <div className={classes.ComposerToolbar}>
                                <button
                                    type="button"
                                    className={classes.IconBtn}
                                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                    title={intl.formatMessage({ id: 'SupportWidget.action.attachFile', defaultMessage: 'Прикрепить файл' })}
                                >
                                    <IconPaperclip />
                                </button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    className={classes.HiddenFile}
                                    onChange={(e) => onPickFiles(e.target.files)}
                                />
                                <button
                                    className={classes.SendBtn}
                                    disabled={sending}
                                    onClick={view === VIEW_NEW ? submitNewTicket : submitMessage}
                                >
                                    <IconSend />
                                    {sending
                                        ? intl.formatMessage({ id: 'SupportWidget.action.sending', defaultMessage: 'Отправка...' })
                                        : view === VIEW_NEW
                                          ? intl.formatMessage({
                                                id: 'SupportWidget.action.createTicket',
                                                defaultMessage: 'Создать обращение',
                                            })
                                          : intl.formatMessage({
                                                id: 'SupportWidget.action.send',
                                                defaultMessage: 'Отправить',
                                            })}
                                </button>
                            </div>
                        </div>
                    )}

                    {view === VIEW_CHAT && activeTicket && activeTicket.status === 'closed' && (
                        <div className={classes.ComposerClosedNote}>
                            {intl.formatMessage({
                                id: 'SupportWidget.closedNote',
                                defaultMessage: 'Обращение закрыто. Создайте новое, если нужна помощь.',
                            })}
                        </div>
                    )}
                </div>
            )}
        </Fragment>
    );
};

export default withAuth(SupportWidget);



// WEBPACK FOOTER //
// ./src/Components/SupportWidget/index.js